/* eslint-disable no-param-reassign */
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { usePanel } from 'contexts/panelContext';
import { useGlobal } from 'contexts/globalContext';
import { userPanelApi } from 'api';
import { SessionType } from 'types/Session';
import { Goal } from 'types/Goal';
import { QuestionnaireAnswer, SessionQuestionnaire } from 'types/SessionQuestionnaire';
import {
	CreateSessionQuestionnairePayload,
	UpdateSessionQuestionnairePayload,
} from 'types/payloads/SessionQuestionnairePayload';
import { SelectButtonGroupYesNo } from 'components/_new/SelectButtonGroup';
import { colors } from 'theme';
import { Box, BoxSection } from 'components/_new/Box';
import { StepSlider } from 'components/_new/Slider';
import { Icon } from 'components/_new/Icon';
import { Line } from 'components/_new/Line';
import { Button } from 'components/_new/Button';
import { InputTextArea } from 'components/_new/InputTextArea';
import { MessageBox, MessageBoxRef } from 'components/_new/MessageBox';
import { usePair } from 'contexts/pairContext';
import { Divider } from 'components/_new/Divider';
import { RouterUrlParams } from '../../../../App';
import { useSessionsData } from '../Sessions';

type ProgressSurveyFormObject = Pick<
	QuestionnaireAnswer,
	| 'goals'
	| 'actions'
	| 'menteeEngagement'
	| 'mentorEngagement'
	| 'menteeSatisfaction'
	| 'standards'
	| 'schedule'
	| 'contract'
	| 'goalChanges'
>;

interface Props {
	pairId?: number;
	sessionType: SessionType;
	questionnairesData?: SessionQuestionnaire[];
	refetchQuestionnaires?: any;
}

const ProgressSurveyModule: FC<Props> = ({ sessionType, questionnairesData, refetchQuestionnaires }) => {
	const { t } = useTranslation();
	const { organizationName, programName, type } = useParams() as RouterUrlParams;

	const { toastRef } = useGlobal();
	const { currentProgramMembership, panelType } = usePanel();
	const {
		pair: { id: pairId },
	} = usePair();
	const { sessiosRefetch } = useSessionsData();

	const currentQuestionnaire = questionnairesData?.find((questionnaire) => questionnaire.forMentorOnly === false);
	const questionnaireAnswers = currentQuestionnaire?.questionnaireAnswers;
	const questionaireGoalsAnswers = questionnaireAnswers?.goals;
	const questionaireActionsAnswers = questionnaireAnswers?.actions;
	const { data: goalsData, isSuccess: goalsSuccess } = useQuery(
		['goals', { programMembershipId: currentProgramMembership.id, pairId }],
		() => userPanelApi.getGoals(pairId, currentProgramMembership.id),
		{
			refetchOnWindowFocus: false,
			enabled: true,
		}
	);

	const autosaveMessageRefs = useRef<Record<string, MessageBoxRef>>({});

	const defaultValues: Partial<ProgressSurveyFormObject> = useMemo(() => {
		const goalsDefaultValues =
			goalsData?.goals.reduce(
				(goalsValueObject, goal) => ({
					...goalsValueObject,
					[`goal-${goal.id}`]: (questionaireGoalsAnswers && questionaireGoalsAnswers[`goal-${goal.id}`]) ?? 0,
				}),
				{}
			) || {};
		const actionsDefaultValues =
			goalsData?.goals.reduce<Record<string, number>>((actionsValueObject, goal) => {
				const goalActions = goal.actions || [];

				goalActions.forEach((action) => {
					actionsValueObject[`action-${action.id}`] =
						(questionaireActionsAnswers && questionaireActionsAnswers[`action-${action.id}`]) ?? 0;
				});

				return actionsValueObject;
			}, {}) || {};

		const baseValues: Partial<ProgressSurveyFormObject> = {
			goals: goalsDefaultValues,
			actions: actionsDefaultValues,
			menteeEngagement: (questionnaireAnswers?.menteeEngagement as number) ?? 0,
			mentorEngagement: (questionnaireAnswers?.mentorEngagement as number) ?? 0,
			menteeSatisfaction: (questionnaireAnswers?.menteeSatisfaction as number) ?? 0,
		};

		if (sessionType === 'last') {
			return {
				...baseValues,
				standards: questionnaireAnswers?.standards as boolean | undefined,
				schedule: questionnaireAnswers?.schedule as boolean | undefined,
				contract: questionnaireAnswers?.contract as boolean | undefined,
				goalChanges: (questionnaireAnswers?.goalChanges as string) ?? '',
			};
		}

		return baseValues;
	}, [goalsData?.goals, questionaireActionsAnswers, questionaireGoalsAnswers, questionnaireAnswers, sessionType]);

	const { control, watch, handleSubmit, reset, getValues } = useForm<Partial<ProgressSurveyFormObject>>({
		defaultValues,
	});

	useEffect(() => {
		reset(defaultValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultValues]);

	const { mutate: createQuestionnaire, isLoading: isCreateSubmitting } = useMutation(
		({ data, showToast = true }: { data: CreateSessionQuestionnairePayload; showToast?: boolean }) =>
			userPanelApi.createQuestionnaire(+pairId, currentProgramMembership.id, data),
		{
			onSuccess: (data, variables) => {
				refetchQuestionnaires();
				sessiosRefetch();
				if (variables.showToast) {
					toastRef?.current?.show({
						severity: 'success',
						life: 3000,
						summary: t('misc.success'),
						detail: t('userPanel.sessions.successfullSessionEditTooltip'),
					});
				}
			},
		}
	);

	const { mutate: updateQuestionnaire, isLoading: isUpdateSubmitting } = useMutation(
		({ data, showToast = true }: { data: UpdateSessionQuestionnairePayload; showToast?: boolean }) =>
			userPanelApi.updateQuestionnaire(+pairId, currentProgramMembership.id, sessionType, data),
		{
			onSuccess: (data, variables) => {
				refetchQuestionnaires();
				sessiosRefetch();

				if (variables.showToast) {
					toastRef?.current?.show({
						severity: 'success',
						life: 3000,
						summary: t('misc.success'),
						detail: t('userPanel.sessions.successfullSessionEditTooltip'),
					});
				}
			},
		}
	);

	const autosave = (fieldName: string) => {
		const value = getValues(fieldName as any);
		let defaultValue: any;

		if (fieldName.startsWith('goals.goal-')) {
			const goalKey = fieldName.replace('goals.', '');
			defaultValue = defaultValues.goals?.[goalKey];
		} else if (fieldName.startsWith('actions.action-')) {
			const actionKey = fieldName.replace('actions.', '');
			defaultValue = defaultValues.actions?.[actionKey];
		} else {
			defaultValue = defaultValues[fieldName as keyof ProgressSurveyFormObject];
		}

		if (value !== defaultValue) {
			let updatePayload: any = {};

			if (fieldName.startsWith('goals.goal-')) {
				const currentGoals = getValues('goals');
				updatePayload = { goals: currentGoals };
			} else if (fieldName.startsWith('actions.action-')) {
				const currentActions = getValues('actions');
				updatePayload = { actions: currentActions };
			} else {
				updatePayload = { [fieldName]: value };
			}

			if (currentQuestionnaire) {
				updateQuestionnaire({
					data: {
						id: currentQuestionnaire.id,
						questionnaireAnswers: updatePayload,
					},
					showToast: false,
				});
			} else {
				createQuestionnaire({
					data: {
						sessionType,
						forMentorOnly: false,
						questionnaireAnswers: { ...getValues(), ...updatePayload },
					},
					showToast: false,
				});
			}

			autosaveMessageRefs.current[fieldName]?.show();
		}
	};

	const onSubmit = handleSubmit((data) => {
		if (!questionnairesData?.length || !currentQuestionnaire) {
			createQuestionnaire({
				data: {
					sessionType,
					forMentorOnly: false,
					questionnaireAnswers: data,
				},
				showToast: true,
			});
		} else {
			updateQuestionnaire({
				data: {
					id: currentQuestionnaire.id,
					questionnaireAnswers: { ...data },
				},
				showToast: true,
			});
		}
	});

	const renderSliderLabel = (goal: Goal, index: number) => (
		<div className="flex flex-column row-gap-3">
			<div className="flex align-items-start column-gap-3">
				<div className="flex-shrink-0 min-w-max">{`${t('userPanel.goal.goal')} #${index + 1}`}</div>

				<div className="flex flex-column row-gap-2 flex-1">
					<div>{`${t('userPanel.goal.goal')}: ${goal.goal}`}</div>
					<div>
						{`${t('userPanel.sessions.mentorEvaluation.criteria')}: `}
						{goal.change || (
							<Trans
								t={t}
								i18nKey="userPanel.sessions.mentorEvaluation.noCriteria"
								components={[
									<Link
										to={`/panel/${organizationName}/${programName}/${type}/pair/${pairId}/mentee-plan`}
										className="text-purplish-blue underline"
									/>,
								]}
							/>
						)}
					</div>
					<div style={{ color: colors.gray }}>
						{`${t('userPanel.sessions.mentorEvaluation.goalRealization')}: ${
							((watch(`goals.goal-${goal.id}`) as number) ?? 0) * 10
						}%`}
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<form className="flex flex-column row-gap-5 pt-3" onSubmit={onSubmit}>
			{goalsSuccess && goalsData && (
				<>
					{goalsData.goals.length === 0 && (
						<MessageBox
							variant="purple"
							title={t('userPanel.sessionsNew.progressSurvey.enterGoalsInMenteeDevelopmentPlan')}
							icon="info-circle"
							iconSet="pi"
						/>
					)}
					{goalsData.goals.length > 0 && (
						<Box variant="white-bordered">
							{goalsData.goals.map((goal, index) => {
								const goalActions = goal.actions || [];
								const hasActions = goalActions.length > 0;

								return (
									<React.Fragment key={goal.id}>
										<Controller
											name={`goals.goal-${goal.id}`}
											control={control}
											render={({ field: { ref, onChange, value } }) => (
												<BoxSection
													header={() => (
														<div className="flex flex-row gap-2 justify-content-start align-items-center">
															{renderSliderLabel(goal, index)}
															{!value && (
																<Icon name="circle" className="text-xs text-rose" />
															)}
														</div>
													)}
													headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
													contentClassName="pb-2"
												>
													<StepSlider
														ref={ref}
														min={0}
														max={10}
														onChange={onChange}
														onChangeCommitted={() => autosave(`goals.goal-${goal.id}`)}
														defaultValue={Number(value || 0)}
													/>
													<MessageBox
														ref={(el: MessageBoxRef) => {
															if (el) {
																autosaveMessageRefs.current[`goals.goal-${goal.id}`] =
																	el;
															}
														}}
														message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
														variant="success"
														autoHide
														autoHideDuration={3000}
														className="mb-2"
														tiny
													/>
												</BoxSection>
											)}
										/>
										{hasActions && (
											<BoxSection
												header={t(
													'userPanel.sessions.progressSurvey.questionnaire.actionsEvaluation'
												)}
												headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
												contentClassName="pb-2"
											>
												<div className="flex flex-column row-gap-4">
													{goalActions.map((action) => (
														<Controller
															key={action.id}
															name={`actions.action-${action.id}`}
															control={control}
															render={({ field: { ref, onChange, value } }) => (
																<div className="flex flex-column row-gap-2">
																	<div className="flex align-items-center gap-2">
																		<span>{action.text}</span>
																		{!value && (
																			<Icon
																				name="circle"
																				className="text-xs text-rose"
																			/>
																		)}
																	</div>
																	<StepSlider
																		ref={ref}
																		min={0}
																		max={10}
																		onChange={onChange}
																		onChangeCommitted={() =>
																			autosave(`actions.action-${action.id}`)
																		}
																		defaultValue={Number(value || 0)}
																	/>
																	<MessageBox
																		ref={(el: MessageBoxRef) => {
																			if (el) {
																				autosaveMessageRefs.current[
																					`actions.action-${action.id}`
																				] = el;
																			}
																		}}
																		message={t(
																			'userPanel.sessionsNew.reflection.autosaveMessage'
																		)}
																		variant="success"
																		autoHide
																		autoHideDuration={3000}
																		className="mb-2"
																		tiny
																	/>
																</div>
															)}
														/>
													))}
												</div>
											</BoxSection>
										)}
										{index < goalsData.goals.length - 1 && <Divider />}
									</React.Fragment>
								);
							})}
						</Box>
					)}
				</>
			)}

			{['menteeEngagement', 'mentorEngagement', 'menteeSatisfaction'].map((name) => (
				<Box key={name} variant="white-bordered">
					<Controller
						name={name as keyof ProgressSurveyFormObject}
						control={control}
						render={({ field: { ref, onChange, value } }) => (
							<BoxSection
								header={() => (
									<div className="flex flex-row gap-2 justify-content-start align-items-center">
										{t(`userPanel.sessions.progressSurvey.questionnaire.${name}`)}
										{!value && <Icon name="circle" className="text-xs text-rose" />}
									</div>
								)}
								headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
								contentClassName="pb-2"
							>
								<StepSlider
									ref={ref}
									min={0}
									max={10}
									onChange={onChange}
									onChangeCommitted={() => autosave(name)}
									defaultValue={Number(value || 0)}
								/>
								<MessageBox
									ref={(el: MessageBoxRef) => {
										if (el) {
											autosaveMessageRefs.current[name] = el;
										}
									}}
									message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
									variant="success"
									autoHide
									autoHideDuration={3000}
									className="mb-2"
									tiny
								/>
							</BoxSection>
						)}
					/>
				</Box>
			))}

			{sessionType === 'last' && (
				<>
					{['standards', 'schedule', 'contract'].map((name) => (
						<Controller
							key={name}
							name={name as keyof ProgressSurveyFormObject}
							control={control}
							render={({ field }) => {
								const { value, onChange } = field;
								return (
									<div key={name} className="flex flex-row gap-2 align-items-center">
										<div className="">
											<h3 className="font-semibold heading-16-24 text-on-blue-grey">
												{t(`userPanel.sessions.progressSurvey.questionnaire.${name}`)}
											</h3>
										</div>
										{value === undefined && <Icon name="circle" className="text-xs text-rose" />}
										<Line className="flex-1" minWidth={20} />
										<SelectButtonGroupYesNo
											value={
												typeof value !== 'undefined' && value !== null ? Boolean(value) : null
											}
											onChange={({ value: newValue }) => {
												onChange(newValue);
												autosave(name);
											}}
										/>
									</div>
								);
							}}
						/>
					))}
					<Box variant="white-bordered">
						<Controller
							name="goalChanges"
							control={control}
							render={({ field: { value, ...restField } }) => (
								<BoxSection
									header={() => (
										<div className="flex flex-row gap-2 justify-content-start align-items-center">
											{t('userPanel.sessions.progressSurvey.questionnaire.goalChanges')}
											{!value && <Icon name="circle" className="text-xs text-rose" />}
										</div>
									)}
									headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
									contentClassName="pb-2"
								>
									<InputTextArea
										{...restField}
										value={value}
										defaultValue={value}
										onBlur={() => {
											autosave('goalChanges');
										}}
									/>
									<MessageBox
										ref={(el: MessageBoxRef) => {
											if (el) {
												autosaveMessageRefs.current.goalChanges = el;
											}
										}}
										message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
										variant="success"
										autoHide
										autoHideDuration={3000}
										className="mb-2"
										tiny
									/>
								</BoxSection>
							)}
						/>
					</Box>
				</>
			)}

			<div className="flex justify-content-end">
				<Button submit label={t('misc.forms.save')} loading={isCreateSubmitting || isUpdateSubmitting} />
			</div>
		</form>
	);
};

export default ProgressSurveyModule;
